import { HttpClient, HttpContext, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { OMITIR_ERROR_GLOBAL } from '../http-context';

export interface DocumentoOferta {
  id: string;
  titulo: string;
  descripcion: string;
  url: string;
  obligatorio: boolean;
}

export interface RespuestaCodigo {
  ok: boolean;
  correoEnmascarado: string;
  largoCodigo: number;
  vigenciaSegundos: number;
  intentosMaximos: number;
  mensaje?: string;
}

export interface ResultadoConfirmacion {
  folio: string;
  acreedor: string;
  montoTotalPesos: number;
  montoLiquidoPesos: number;
  estado: string;
  correoNotificacion: string;
  fechaMaterializacion: string;
  documentos: DocumentoOferta[];
  liquidoConCompensacionPesos: number;
  liquidoSinCompensacionPesos: number;
  deudaCompensarPesos: number;
  compensando: boolean;
  montoTotalDescuentoSinCompensacionClp: number;
  montoTotalDescuentoConCompensacionClp: number;
}

/**
 * GET /pronto-pago/comprobante (vista final o post aceptación)
 */
export interface ProntoPagoComprobante {
  folio: string;
  acreedor: string;
  correo: string;
  fechaPago: string;
  montoTotal: number;
  montoLiquido: number;
  estado: number;
  glosaEstado: string;
  urlAcuerdo: string;
  liquidoConCompensacionPesos: number;
  liquidoSinCompensacionPesos: number;
  montoTotalDeudaCompensarClp: number;
  aceptaCompensacion: boolean;
  montoTotalDescuentoSinCompensacionClp: number;
  montoTotalDescuentoConCompensacionClp: number;
}

/**
 * Respuesta REAL del backend para la verificación por correo. Ambos endpoints informan el
 * resultado en `codigoRetorno` y `glosaRetorno`, y responden 200 aunque no se envíe el código:
 *   - POST /pronto-pago/generar-clave → 0 = enviado (trae `correo` enmascarado y `fechaCaducidad`),
 *     1 = el afiliado no tiene correo registrado, 2 = superó el máximo de envíos por hora.
 *     Con 1 y 2 el SP no genera el código; con 2 igual viene el `correo`.
 *   - POST /pronto-pago/validar-clave → 0 = clave correcta.
 */
export interface ProntoPagoVerificacion {
  rut?: string;
  correo?: string;
  fechaCaducidad?: string;
  codigoRetorno?: number;
  glosaRetorno?: string;
}

/**
 * Falla del envío del código. Ninguno de los dos primeros motivos se arregla reintentando:
 * `sin-correo` es definitivo y `limite-envios` sólo cede al pasar la ventana horaria del back.
 */
export type MotivoErrorEnvio = 'sin-correo' | 'limite-envios' | 'error';

export interface ErrorEnvioCodigo {
  motivo: MotivoErrorEnvio;
  mensaje: string;
}

/**
 * POST /pronto-pago/aceptar — registra la aceptación de la propuesta.
 */
export interface ProntoPagoAceptacion {
  rut?: string;
  compensaDeuda: boolean;
  aceptado?: boolean;
}

/** 0 = sin compensar la deuda · 1 = compensando la deuda. */
export type CodTipoDetalle = 0 | 1;

/**
 * GET /pronto-pago/resumen — flags del banner + las 3 tarjetas (Total / Llevamos /
 * Restantes) + datos del titular + URLs de PDF.
 */
export interface ProntoPagoResumen {
  nombrePila: string;
  titular: string;
  rut: string;
  correoPersonal: string;
  afiliadoVigente: boolean;
  ofertaVigente: boolean;
  aceptaOferta: boolean;
  aceptaCompensacion: boolean;
  tieneDeuda: boolean;
  totalCuotas: number;
  cuotasPagadas: number;
  cuotasPendientes: number;
  totalUf: number;
  pagadoUf: number;
  devengarUf: number;
  montoDevengarClp: number;
  montoTotalDeudaCompensarClp: number;
  liquidoPesos: number;
  fechaVigenciaOferta: string; // reemplazado por campanaVigente (comprueba fechas en SP)
  fechaPago: string;
  fechaInicioCmpna: string;
  fechaFinCmpna: string;
  campanaVigente: boolean;
  urlOferta: string;
  urlAcuerdo: string;
  ufReferencia: number;
  liquidoConCompensacionUf: number;
  liquidoConCompensacionPesos: number;
  liquidoSinCompensacionUf: number;
  liquidoSinCompensacionPesos: number;
  montoTotalDescuentoSinCompensacionClp: number;
  montoTotalDescuentoConCompensacionClp: number;
}

/** GET /pronto-pago/detalle-cuotas — devuelve AMBAS modalidades; el front filtra por codTipoDetalle. */
export interface ProntoPagoCuota {
  numCuota: number;
  periodoCuota: string;
  codTipoDetalle: CodTipoDetalle;
  cantidadDevengarUf: number;
  montoDevengarClp: number;
  montoDescuentoClp: number;
  montoNetoDevengado: number;
  glsTipoPago: string;
}

/**
 * GET /pronto-pago/detalle-deuda — deuda del afiliado (tabla "Tu deuda con Colmena").
 */
export interface ProntoPagoDeuda {
  conceptoDeuda: string;
  montoDeudaBrutoClp: number;
  montoDeudaBrutoUf: number;
  montoInteresesClp: number;
  montoInteresesUf: number;
  montoRecargosClp: number;
  montoRecargosUf: number;
  montoDeudaTotalClp: number;
  montoDeudaTotalUf: number;
  detalles: ProntoPagoDeudaDetalle[];
}

/** Desglose de un grupo de deuda (una deuda individual dentro de `ProntoPagoDeuda`). */
export interface ProntoPagoDeudaDetalle {
  idDeuda: number;
  conceptoDeuda: string;
  detalleDeuda: string;
  montoDeudaBrutoClp: number;
  montoDeudaBrutoUf: number | null;
  montoInteresesClp: number;
  montoInteresesUf: number | null;
  montoRecargosClp: number;
  montoRecargosUf: number | null;
  montoDeudaTotalClp: number;
  montoDeudaTotalUf: number | null;
}

@Injectable({
  providedIn: 'root'
})
export class ProntoPagoRestitucionService {

  private url = environment.server + environment.app;

  private readonly largoCodigo = 8;
  private readonly intentosMaximos = 3;
  private readonly vigenciaFallbackSeg = 600;

  constructor(private http: HttpClient) { }

  private get contexto(): HttpContext {
    return new HttpContext().set(OMITIR_ERROR_GLOBAL, true);
  }

  enviarCodigoVerificacion(): Observable<RespuestaCodigo> {
    return this.http.post<ProntoPagoVerificacion>(`${this.url}pronto-pago/generar-clave`, {},
      { context: this.contexto }).pipe(
        catchError(this.handleError),
        switchMap(v => this.envioExitoso(v)
          ? of(this.aRespuestaCodigo(v))
          : throwError((): ErrorEnvioCodigo => this.aErrorEnvio(v)))
      );
  }

  validarCodigo(codigo: string): Observable<{ valido: boolean; mensaje?: string }> {
    return this.http.post<ProntoPagoVerificacion>(`${this.url}pronto-pago/validar-clave`, { clave: codigo },
      { context: this.contexto }).pipe(
        map(v => {
          const valido = v.codigoRetorno === 0;
          return {
            valido,
            mensaje: valido ? undefined : (v.glosaRetorno || 'El código ingresado no es válido.')
          };
        }),
        catchError(this.handleError)
      );
  }

  aceptarOferta(compensaDeuda: boolean): Observable<ProntoPagoAceptacion> {
    return this.http.post<ProntoPagoAceptacion>(`${this.url}pronto-pago/aceptar`, { compensaDeuda },
      { context: this.contexto }).pipe(catchError(this.handleError));
  }

  descargarAcuerdo(compensando: boolean): Observable<HttpResponse<Blob>> {
    return this.http.post(`${this.url}pronto-pago/acuerdo`, { conCompensacion: compensando },
      { responseType: 'blob', observe: 'response', context: this.contexto })
      .pipe(catchError(this.handleError));
  }

  /** GET /pronto-pago/comprobante — datos para la vista final. */
  getComprobante(): Observable<ResultadoConfirmacion> {
    return this.http.get<ProntoPagoComprobante>(`${this.url}pronto-pago/comprobante`,
      { context: this.contexto }).pipe(
        map(c => this.aResultadoConfirmacion(c)),
        catchError(this.handleError)
      );
  }

  /** GET /pronto-pago/resumen — cabecera (3 cajas), titular y estado de la oferta. */
  getResumen(): Observable<ProntoPagoResumen> {
    return this.http.get<ProntoPagoResumen>(`${this.url}pronto-pago/resumen`,
      { context: this.contexto }).pipe(catchError(this.handleError));
  }

  /** GET /pronto-pago/detalle-cuotas — AMBAS modalidades; el front filtra por codTipoDetalle. */
  getDetalleCuotas(): Observable<ProntoPagoCuota[]> {
    return this.http.get<ProntoPagoCuota[]>(`${this.url}pronto-pago/detalle-cuotas`,
      { context: this.contexto }).pipe(catchError(this.handleError));
  }

  /** GET /pronto-pago/detalle-deuda — deuda del afiliado (tablas "Tu deuda con Colmena"). */
  getDetalleDeuda(): Observable<ProntoPagoDeuda[]> {
    return this.http.get<ProntoPagoDeuda[]>(`${this.url}pronto-pago/detalle-deuda`,
      { context: this.contexto }).pipe(catchError(this.handleError));
  }

  private aResultadoConfirmacion(c: ProntoPagoComprobante): ResultadoConfirmacion {
    return {
      folio: c.folio,
      acreedor: c.acreedor,
      montoTotalPesos: c.montoTotal,
      montoTotalDescuentoSinCompensacionClp: c.montoTotalDescuentoSinCompensacionClp,
      montoTotalDescuentoConCompensacionClp: c.montoTotalDescuentoConCompensacionClp,
      montoLiquidoPesos: c.montoLiquido,
      estado: c.glosaEstado,
      correoNotificacion: c.correo,
      fechaMaterializacion: c.fechaPago,
      documentos: [
        { id: 'acuerdo', titulo: 'Acuerdo Pago Anticipado.pdf', descripcion: 'Acuerdo firmado', url: c.urlAcuerdo, obligatorio: true }
      ],
      liquidoConCompensacionPesos: c.liquidoConCompensacionPesos,
      liquidoSinCompensacionPesos: c.liquidoSinCompensacionPesos,
      deudaCompensarPesos: c.montoTotalDeudaCompensarClp,
      compensando: c.aceptaCompensacion
    };
  }

  private tieneCorreo(v: ProntoPagoVerificacion): boolean {
    return !!v.correo && v.correo.trim().length > 0;
  }

  private envioExitoso(v: ProntoPagoVerificacion): boolean {
    const retornoOk = v.codigoRetorno === null || v.codigoRetorno === undefined || v.codigoRetorno === 0;
    return retornoOk && this.tieneCorreo(v);
  }

  private aErrorEnvio(v: ProntoPagoVerificacion): ErrorEnvioCodigo {
    return {
      motivo: this.motivoError(v),
      mensaje: v.glosaRetorno || 'No pudimos enviar el código a tu correo.'
    };
  }

  private motivoError(v: ProntoPagoVerificacion): MotivoErrorEnvio {
    if (v.codigoRetorno === 2) {
      return 'limite-envios';
    }
    // El back sólo omite el correo cuando no hay ninguno registrado.
    if (v.codigoRetorno === 1 || !this.tieneCorreo(v)) {
      return 'sin-correo';
    }
    return 'error';
  }

  private aRespuestaCodigo(v: ProntoPagoVerificacion): RespuestaCodigo {
    return {
      ok: true,
      correoEnmascarado: v.correo || '',
      largoCodigo: this.largoCodigo,
      vigenciaSegundos: this.vigenciaDesde(v.fechaCaducidad),
      intentosMaximos: this.intentosMaximos,
      mensaje: v.glosaRetorno
    };
  }

  private vigenciaDesde(fechaCaducidad?: string): number {
    if (!fechaCaducidad) {
      return this.vigenciaFallbackSeg;
    }
    const ms = new Date(fechaCaducidad).getTime() - Date.now();
    return ms > 0 ? Math.round(ms / 1000) : 0;
  }

  private handleError(error: HttpErrorResponse) {
    if (error.error instanceof ErrorEvent) {
      console.error('Ocurrió un error:', error.error.message);
    } else {
      console.error(`Código de estado del backend: ${error.status}, cuerpo: ${error.error}`);
    }
    return throwError(() => 'Hubo un problema con la solicitud; por favor intenta nuevamente más tarde.');
  }
}
